import LivePreviewBase from "./live_preview_base"
import {
  applyTextStrokeToElements,
  applyFontSizeScale,
  applyTypographyToElements,
  applyTextShadowToElements,
} from "../utils/text_treatment"
import { TYPE_PAIR_SPECS } from "../services/language_learning_type_pairs"

// Live-preview controller for the Collocation wizard.
//
// The preview mirrors FRAME 0, and on this template frame 0 is the PAYOFF pole
// with no withhold at all: the node chip, the STRUCK rival chip, the trap note,
// every composed chunk with its node span tinted, and every meaning, all painted
// in full ink under the header (flag, language, level badge, ×N count pill, i/N
// step pill, GOES TOGETHER badge). Every applier here therefore REPAINTS
// something that is already visible. Five consequences, each a law rather than a
// style choice:
//
//   1. Nothing here may touch `opacity`, ever. The template has no fade-in and
//      no fade-out anywhere: the walk is a background/colour/scale change and
//      the intro cues are colour-and-scale only. There is no opacity axis to
//      animate — which is also why the rival chip and the gloss lines toggle via
//      the `hidden` ATTRIBUTE rather than by fading.
//   2. The rival chip is STRUCK from the first frame and stays struck. Nothing
//      here un-strikes it, and nothing delays the strike: an un-struck rival
//      that gets struck later reads, for the first second, as "this one is
//      right" — the inversion the whole chip exists to prevent. Clearing
//      `trap_word` removes the chip outright (and its note with it), which is
//      the props' own omit-when-blank contract.
//   3. Danger ink appears in exactly ONE subtree — the rival chip — and it is
//      resolved per theme by `dangerInkFor` so it can never equal the palette
//      accent. `applyTheme` repaints both tokens together, so the pair can never
//      drift apart mid-edit.
//   4. The chunk rows ARE live-bound, unlike Substitution Drill's option grid.
//      T4's slot box is pinned to the WIDEST option, so its fit depends on the
//      row text and a JS copy would have been a third derivation. T7's solver is
//      `clamp(ROW_MIN, budget / count, ROW_MAX)` — it depends on the COUNT
//      alone, and the count only moves through a server round trip (add /
//      remove). Nothing derived depends on the row TEXT, so mirroring it live is
//      safe rather than a second source of truth.
//   5. The join is a JOIN, never a parse. The two halves of every chunk live in
//      their own spans and are re-ordered in place when `anchor_position` flips;
//      the controller never splits a rendered string back apart.
//
// The trap chunk is derived exactly as the builder derives it: composed chunk 1
// with its LEADING element replaced by the rival — row 1's partner under `lead`
// ("do a mistake"), the node itself under `trail` ("strong rain").
//
// The two languages are asymmetric, as everywhere in the LL kit:
// `target_language_code` is the language of everything VOICED (hero flag +
// language label) and `learner_locale` is the language of everything READ
// (glosses, badges) — which is why it repaints the badge, the never-label and
// the language label.
//
// TYPE_PAIR_SPECS is imported from the shared language-learning catalog
// (de-mirrored — the font data is authored once in the TS source and consumed by
// both legs), so the preview can never drift from what the renderer paints.
//
// Mounted alongside `palette-live-preview`, which owns the background layers +
// tint/darkness sliders. Inherits event wiring + dispatch + value normalisation
// from LivePreviewBase.
export default class extends LivePreviewBase {
  static targets = [
    "frame", "flag", "languageLabel", "chunkBadge", "levelBadge", "countPill", "stepPill",
    "hookText",
    "nodeRow", "nodeWord", "rivalChip", "neverLabel", "rivalChunk", "trapNote",
    "chunkText", "chunkGloss",
    "ctaText",
  ]

  static FIELD_BINDINGS = {
    hook_text:            { apply: "applyHookText" },
    anchor_text:          { apply: "applyAnchor" },
    anchor_position:      { apply: "applyAnchorPosition" },
    trap_word:            { apply: "applyTrap" },
    trap_note:            { apply: "applyTrapNote" },
    cta_text:             { apply: "applyCtaText" },
    difficulty:           { apply: "applyDifficulty" },
    item_partner:         { apply: "applyPartner" },
    item_gloss:           { apply: "applyGloss" },
    target_language_code: { apply: "applyTargetLanguage" },
    learner_locale:       { apply: "applyLearnerLocale" },
    theme:                { apply: "applyTheme" },
    type_pair:            { apply: "applyTypography" },
    italic_override:      { apply: "applyTypography" },
    font_size_scale:      { apply: "applyFontSize" },
    display_override:     { apply: "applyFontSize" },
    body_override:        { apply: "applyFontSize" },
    text_shadow:          { apply: "applyTextShadow" },
    text_stroke_color:    { apply: "applyStroke" },
    text_stroke_width:    { apply: "applyStroke" },
  }

  // -- chrome ---------------------------------------------------------------

  applyHookText() {
    this.setText("hookText", stripLanguageTags(this.fieldValue("hook_text")) || "Your hook goes here.")
  }

  applyCtaText() { this.setText("ctaText", this.fieldValue("cta_text")) }

  // The level badge is a locale-free literal (A1..C2), exactly as the renderer
  // paints it — never translated, never re-worded.
  applyDifficulty({ value }) { if (value) this.setText("levelBadge", value) }

  // -- the node, the join, and everything derived from them -----------------

  // The node appears in N + 1 places: the chip, the tinted span inside every
  // chunk, and (under `trail`) the tail of the rival chunk. One field, one
  // repaint of all of them.
  applyAnchor() {
    this.setText("nodeWord", this.anchorText())
    this.repaintChunks()
    this.repaintTrap()
  }

  // Flipping the join re-orders the two spans of EVERY row in place, and moves
  // the rival chunk's tail from row 1's partner to the node itself.
  applyAnchorPosition() {
    this.repaintChunks()
    this.repaintTrap()
  }

  // Row *i*'s partner. Under `lead` row 0 also feeds the rival chunk, so a
  // change there repaints the chip too.
  applyPartner({ event }) {
    const index = rowIndex(event.target)
    if (index === null) return
    this.paintChunk(index, this.partnerAt(index))
    if (index === 0) this.repaintTrap()
  }

  // Omit-when-blank, mirroring the builder's `gloss` key: an empty string would
  // reserve the line and open a hole in the row, so the line goes away entirely.
  applyGloss({ event }) {
    const index = rowIndex(event.target)
    if (index === null) return
    const target = this.chunkGlossTargets.find((el) => rowIndex(el) === index)
    if (!target) return
    const value = event.target.value || ""
    target.textContent = value
    target.hidden = value.trim() === ""
  }

  applyTrap() { this.repaintTrap() }

  applyTrapNote() { this.repaintTrap() }

  // -- languages ------------------------------------------------------------

  // The VOICED language: the hero flag and the language label. Base subtag
  // throughout, so "en-GB" flags as 🇬🇧 — matching the builder's
  // `resolved_target_flag_emoji`, which splits the subtag before the lookup.
  applyTargetLanguage({ value }) {
    this.setText("languageLabel", languageName(value, this.learnerLocale()))
    if (this.hasFlagTarget) this.flagTarget.textContent = FLAGS[baseSubtag(value)] || "🏳️"
  }

  // The READ language — the glosses, the franchise badge and the word on the
  // struck chip all resolve in it.
  applyLearnerLocale({ value }) {
    const locale = baseSubtag(value || "es")
    this.setText("chunkBadge", CHUNK_BADGE[locale] || CHUNK_BADGE.en)
    this.setText("neverLabel", NEVER_LABEL[locale] || NEVER_LABEL.en)
    this.setText("languageLabel", languageName(this.fieldValue("target_language_code"), value))
  }

  // The palette drives every ink on the frame: the node chip, the anchor span in
  // every row, the active-row bar and the landed ticks take `--p-accent`; the
  // chunks take `--p-text`; the glosses take `--p-muted`; and the rival chip
  // takes `--p-danger`, which is RESOLVED from the palette rather than fixed —
  // on a red-accent look (whiteboard) the raw danger token would land on the
  // accent, i.e. "wrong" and "right" would read as one colour.
  applyTheme({ value }) {
    if (!this.hasFrameTarget) return
    const palette = THEME_PALETTES[value] || THEME_PALETTES.notebook
    const f = this.frameTarget
    f.style.setProperty("--p-bg", palette.bg)
    f.style.setProperty("--p-surface", palette.surface)
    f.style.setProperty("--p-text", palette.text)
    f.style.setProperty("--p-muted", palette.muted)
    f.style.setProperty("--p-accent", palette.accent)
    f.style.setProperty("--p-danger", dangerInkFor(palette))
  }

  // -- typography / font-size / stroke --------------------------------------

  // DISPLAY role: the hook and the node chip (the two heroes). BODY role: the
  // chunk rows, every gloss, the pills, the badge, the trap note and the rival
  // chip — the renderer sets the rows in the body face for the same reason, they
  // have to stay readable at row height with N of them on screen.
  applyTypography() {
    const spec = TYPE_PAIR_SPECS[this.fieldValue("type_pair") || "default"]
    if (!spec) return
    applyTypographyToElements(
      { display: this.displayTargets(), body: this.bodyTargets() },
      spec,
      this.fieldValue("italic_override") || "auto",
    )
  }

  applyFontSize() {
    const globalScale = positiveOr(this.fieldValue("font_size_scale"), 1)
    const displayScale = this.readPositiveOverride("display_override") ?? globalScale
    const bodyScale    = this.readPositiveOverride("body_override")    ?? globalScale

    applyFontSizeScale(this.displayTargets().map((el) => sizedPair(el, 13)), displayScale)
    applyFontSizeScale(this.bodyTargets().map((el) => sizedPair(el, 9)), bodyScale)
  }

  // Stroke rides the opted-in elements only — the hook, the node chip and every
  // chunk row (the surfaces that can sit over a background asset). The badge,
  // the pills, the glosses, the trap note and the RIVAL CHIP skip, mirroring the
  // renderer's applyStroke: false on those roles: a stroked strike-through reads
  // as a smear, which is the one thing a verdict may not read as.
  applyStroke() {
    const targets = Array.from(
      this.element.querySelectorAll("[data-stroke-target]"),
    ).map((el) => ({
      element: el,
      basePx: parseFloat(el.dataset.strokeBasePx) || undefined,
      skip: el.dataset.strokeSkip === "true",
    }))
    if (!targets.length) return
    applyTextStrokeToElements(targets, {
      width: this.fieldValue("text_stroke_width"),
      color: this.fieldValue("text_stroke_color") || "#000000",
    })
  }

  applyTextShadow({ value }) {
    applyTextShadowToElements(
      Array.from(this.element.querySelectorAll("[data-stroke-target]")),
      value,
    )
  }

  readPositiveOverride(fieldName) {
    for (const el of this.fieldElements(fieldName)) {
      if (el.type === "range" && !el.disabled) {
        const v = parseFloat(el.value)
        return Number.isFinite(v) && v > 0 ? v : null
      }
    }
    return null
  }

  // -- derived state --------------------------------------------------------

  anchorText() { return (this.fieldValue("anchor_text") || "").trim() }

  anchorPosition() {
    const stored = (this.fieldValue("anchor_position") || "").trim()
    return stored === TRAIL ? TRAIL : LEAD
  }

  trail() { return this.anchorPosition() === TRAIL }

  learnerLocale() { return this.fieldValue("learner_locale") || "es" }

  partnerAt(index) {
    const input = Array.from(this.fieldElements("item_partner")).find((el) => rowIndex(el) === index)
    return input ? input.value : ""
  }

  repaintChunks() {
    this.chunkTextTargets.forEach((el) => {
      const index = rowIndex(el)
      if (index === null) return
      this.paintChunk(index, this.partnerAt(index))
    })
  }

  // One row's chunk, laid out the way the composition lays it out: two spans in
  // render order with a single space between them. The spans are MOVED, never
  // rebuilt from a joined string — the composition never parses, and neither
  // does its mirror.
  paintChunk(index, partnerValue) {
    const container = this.chunkTextTargets.find((el) => rowIndex(el) === index)
    if (!container) return

    // Each half is squished, not merely trimmed, so concatenating the two spans
    // with one space reproduces `squish(anchor + " " + partner)` byte for byte —
    // the builder's join, split across two elements.
    const anchor = squish(this.anchorText())
    const partner = squish(partnerValue)
    const [anchorSpan, partnerSpan] = ensureChunkParts(container)

    if (!anchor || !partner) {
      anchorSpan.textContent = ""
      partnerSpan.textContent = ""
      container.replaceChildren(anchorSpan)
      return
    }

    anchorSpan.textContent = anchor
    partnerSpan.textContent = partner
    container.replaceChildren(
      ...(this.trail() ? [partnerSpan, " ", anchorSpan] : [anchorSpan, " ", partnerSpan]),
    )
  }

  // The rival chunk + its note + the chip's very presence. Composed chunk 1 with
  // its LEADING element replaced by the rival:
  //   lead   [anchor][partner₁] → [trap][partner₁]   "do a mistake"
  //   trail  [partner₁][anchor] → [trap][anchor]     "strong rain"
  // A blank rival drops the WHOLE device — chip, strike, label and note — which
  // is the props' own omit-when-blank behaviour, not a preview shortcut.
  repaintTrap() {
    const trap = (this.fieldValue("trap_word") || "").trim()
    const tail = this.trail() ? this.anchorText() : (this.partnerAt(0) || "").trim()
    const chunk = trap && tail ? squish(`${trap} ${tail}`) : ""
    const note = (this.fieldValue("trap_note") || "").trim()

    this.setText("rivalChunk", chunk)
    if (this.hasRivalChipTarget) this.rivalChipTarget.hidden = chunk === ""
    if (this.hasNodeRowTarget) {
      this.nodeRowTarget.classList.toggle("co-preview__noderow--solo", chunk === "")
    }
    if (this.hasTrapNoteTarget) {
      this.trapNoteTarget.textContent = note
      this.trapNoteTarget.hidden = chunk === "" || note === ""
    }
  }

  // -- preview DOM helpers --------------------------------------------------

  displayTargets() {
    return [
      this.hasHookTextTarget ? this.hookTextTarget : null,
      this.hasNodeWordTarget ? this.nodeWordTarget : null,
    ].filter(Boolean)
  }

  // Everything on the theme surface: the badge, the three pills, the never
  // label, the rival chunk, the trap note, every chunk row + gloss, and the CTA.
  bodyTargets() {
    const singles = [
      "chunkBadge", "levelBadge", "countPill", "stepPill",
      "neverLabel", "rivalChunk", "trapNote", "ctaText",
    ]
      .filter((name) => this[`has${capitalize(name)}Target`])
      .map((name) => this[`${name}Target`])
    return [...singles, ...this.chunkTextTargets, ...this.chunkGlossTargets]
  }

  setText(targetName, value) {
    const has = `has${capitalize(targetName)}Target`
    if (this[has]) this[`${targetName}Target`].textContent = value
  }
}

// VideoConfig::CollocationStrategy::ANCHOR_POSITIONS.
const LEAD = "lead"
const TRAIL = "trail"

function capitalize(name) {
  return name[0].toUpperCase() + name.slice(1)
}

// The row a control or a painted span belongs to. `null` rather than 0 for an
// unmarked element, so a missing attribute can never silently repaint row 1.
function rowIndex(element) {
  const raw = element?.dataset?.itemIndex
  if (raw === undefined || raw === "") return null
  const n = parseInt(raw, 10)
  return Number.isInteger(n) ? n : null
}

// The two halves of a chunk, created on demand: a row whose partner was blank at
// server-render time has no spans yet, and typing one has to bring both back.
function ensureChunkParts(container) {
  let anchorSpan = container.querySelector("[data-chunk-part='anchor']")
  let partnerSpan = container.querySelector("[data-chunk-part='partner']")
  if (!anchorSpan) {
    anchorSpan = document.createElement("span")
    anchorSpan.className = "co-preview__anchorspan"
    anchorSpan.dataset.chunkPart = "anchor"
  }
  if (!partnerSpan) {
    partnerSpan = document.createElement("span")
    partnerSpan.className = "co-preview__partnerspan"
    partnerSpan.dataset.chunkPart = "partner"
  }
  return [anchorSpan, partnerSpan]
}

// Ruby's String#squish, mirroring Collocation::ConfigBuilder#compose — the ONLY
// join heuristic in the template, applied to the trap chunk exactly as it is
// applied to every real chunk.
function squish(text) {
  return (text || "").toString().replace(/\s+/g, " ").trim()
}

// "en-GB" → "en". The flag, name and chrome lookups are keyed on the base subtag.
function baseSubtag(code) {
  return (code || "").toString().split("-")[0].toLowerCase()
}

// The label is localized in the VIEWER's language, mirroring
// LanguageNameResolver.call(code, locale: learner_locale) on the server — a Modo
// video (viewer L1 = es) reads "Inglés", not "English".
function languageName(code, learnerLocale) {
  const base = baseSubtag(code)
  const table = LANGUAGE_NAMES[baseSubtag(learnerLocale)] || LANGUAGE_NAMES.en
  return table[base] || LANGUAGE_NAMES.en[base] || base.toUpperCase()
}

// Every scaled element carries its renderer-relative base size in
// `data-base-font-size` (server-rendered), so the JS never restates the
// preview's size table — the fallback is only for a hand-built DOM.
function sizedPair(element, fallbackBasePx) {
  return { element, basePx: parseFloat(element.dataset.baseFontSize) || fallbackBasePx }
}

function positiveOr(value, fallback) {
  if (value === null || value === undefined) return fallback
  const n = typeof value === "number" ? value : parseFloat(value)
  return Number.isFinite(n) && n >= 0 ? n : fallback
}

// Authoring language spans (<en>…</en>, <es>…</es>) are never drawn. T7's
// authoring law says no chunk carries one at all — the builder RAISES on a tag
// in the node or a partner — so stripping is purely so a hand-edited payload
// still draws clean rather than rendering "<en>".
function stripLanguageTags(text) {
  return (text || "").replace(/<\/?[a-zA-Z][a-zA-Z-]*>/g, "").replace(/\s+/g, " ").trim()
}

// ---------------------------------------------------------------------------
// The negative-polarity resolver — the JS leg of `dangerInkFor(palette)` in
// shared/theme/negativePolarity.ts, and of the Ruby leg on
// ShortsWizard::Collocation::LivePreviewComponent.
//
// WRONG THINGS READ RED. Raw DANGER_INK everywhere, EXCEPT on a red-family
// accent (hue within ~25° of the danger token) where it deepens toward the
// palette's own text so wrong-vs-right separation is carried by lightness where
// hue is unavailable. `whiteboard` is the only shipped look that trips it — its
// accent IS #DC2626 — and it resolves to #8D2C35.
// ---------------------------------------------------------------------------
const DANGER_INK = "#E0393E"
const DANGER_HUE_TOLERANCE_DEG = 25
const DANGER_DEEPEN_RATIO = 0.4

export function dangerInkFor(palette) {
  const delta = Math.abs(hueOf(palette.accent) - hueOf(DANGER_INK))
  const separation = Math.min(delta, 360 - delta)
  if (separation > DANGER_HUE_TOLERANCE_DEG) return DANGER_INK
  return mixHex(DANGER_INK, palette.text, DANGER_DEEPEN_RATIO)
}

function rgbOf(hex) {
  let digits = (hex || "").replace("#", "")
  if (digits.length === 3) digits = digits.split("").map((c) => c + c).join("")
  return [0, 2, 4].map((i) => parseInt(digits.slice(i, i + 2), 16) || 0)
}

function hueOf(hex) {
  const [r, g, b] = rgbOf(hex).map((c) => c / 255)
  const max = Math.max(r, g, b)
  const min = Math.min(r, g, b)
  const delta = max - min
  if (delta === 0) return 0
  let hue
  if (max === r) hue = 60 * (((g - b) / delta) % 6)
  else if (max === g) hue = 60 * ((b - r) / delta + 2)
  else hue = 60 * ((r - g) / delta + 4)
  return (hue + 360) % 360
}

// shared/utils/colorMix.ts's contract: componentwise linear interpolation,
// rounded, `ratio` = how far toward `toHex`.
function mixHex(fromHex, toHex, ratio) {
  const a = rgbOf(fromHex)
  const b = rgbOf(toHex)
  const hex = a
    .map((channel, i) => Math.round(channel * (1 - ratio) + b[i] * ratio))
    .map((channel) => Math.min(255, Math.max(0, channel)).toString(16).padStart(2, "0").toUpperCase())
    .join("")
  return `#${hex}`
}

// All seven language-learning looks, including the two channel-locked ones — a
// channel-bound config CAN carry `cafecito` / `modo`, and repainting it as
// notebook would be the preview lying about the render.
const THEME_PALETTES = {
  "notebook":   { "bg": "#FAF7EC", "surface": "#FFFFFF", "text": "#1F2937", "muted": "#6B7280", "accent": "#E8A838" },
  "chalkboard": { "bg": "#1F2A24", "surface": "#243530", "text": "#F4F3EC", "muted": "#C5C2AE", "accent": "#FFE066" },
  "whiteboard": { "bg": "#F8FAFC", "surface": "#FFFFFF", "text": "#111827", "muted": "#4B5563", "accent": "#DC2626" },
  "cork_board": { "bg": "#C08B5C", "surface": "#FEF3C7", "text": "#1C1917", "muted": "#57534E", "accent": "#EAB308" },
  "minimal":    { "bg": "#FFFFFF", "surface": "#F8FAFC", "text": "#0F172A", "muted": "#64748B", "accent": "#6366F1" },
  "cafecito":   { "bg": "#F6EDDF", "surface": "#FFFBF2", "text": "#3B2A1E", "muted": "#6E5A47", "accent": "#C98A2D" },
  "modo":       { "bg": "#1E2733", "surface": "#28323F", "text": "#F2F5F9", "muted": "#A8B4C2", "accent": "#FFB020" },
}

// The franchise pill, mirroring `niche_templates.collocation.chunk_badge` in the
// three shipped locale files. It names the VALUE, not the grammar. Seen, never
// heard.
const CHUNK_BADGE = {
  "en": "GOES TOGETHER",
  "es": "VAN JUNTAS",
}

// The word on the struck chip — `niche_templates.collocation.never_label`. A
// word PLUS the strike, never a bare ✗.
const NEVER_LABEL = {
  "en": "NEVER",
  "es": "NUNCA",
}

// Mirrors config/locales/language_names.{en,es}.yml for the languages the
// content step offers. An unknown pair falls back to the English name, then to
// the upper-cased code — the same degrade the server's `default:` gives.
const LANGUAGE_NAMES = {
  en: {
    "ar": "Arabic", "de": "German", "en": "English", "es": "Spanish", "fr": "French",
    "hi": "Hindi", "it": "Italian", "ja": "Japanese", "ko": "Korean", "nl": "Dutch",
    "pl": "Polish", "pt": "Portuguese", "ru": "Russian", "sv": "Swedish", "th": "Thai",
    "tr": "Turkish", "vi": "Vietnamese", "zh": "Chinese",
  },
  es: {
    "ar": "Árabe", "de": "Alemán", "en": "Inglés", "es": "Español", "fr": "Francés",
    "hi": "Hindi", "it": "Italiano", "ja": "Japonés", "ko": "Coreano", "nl": "Neerlandés",
    "pl": "Polaco", "pt": "Portugués", "ru": "Ruso", "sv": "Sueco", "th": "Tailandés",
    "tr": "Turco", "vi": "Vietnamita", "zh": "Chino",
  },
}

const FLAGS = {
  "ar": "🇸🇦", "de": "🇩🇪", "en": "🇬🇧", "es": "🇪🇸", "fr": "🇫🇷",
  "hi": "🇮🇳", "it": "🇮🇹", "ja": "🇯🇵", "ko": "🇰🇷", "nl": "🇳🇱",
  "pl": "🇵🇱", "pt": "🇵🇹", "ru": "🇷🇺", "sv": "🇸🇪", "th": "🇹🇭",
  "tr": "🇹🇷", "vi": "🇻🇳", "zh": "🇨🇳",
}
